"use client";

import { use } from "react";
import type { UnpublishedWindow } from "@/lib/windows/types";
import type { AreaSymbol } from "@/lib/layouts/types";
import { useAdmin } from "@/lib/admin/context";
import { resolveWindowVariant } from "@/components/cms/registry/variants";
import { resolveContainer } from "@/components/cms/registry/containers";
import type { WindowContentResponse } from "@/lib/api/cms-window-content";
import { useLocale } from "@/lib/i18n/context";
import { getMessages } from "@/lib/i18n";
import styles from "./WindowRenderer.module.css";

/**
 * WindowRenderer — dispatcher renderowania content'u okna na publicznej stronie.
 *
 * **Data flow:**
 *   1. PublicLayout (server) fetcha contents per `id_window` w parallel
 *      (`Promise.all(fetchWindowContent(id))`)
 *   2. Content trafia do `AdminContext.windowContents` (Map<id, response>)
 *   3. WindowRenderer czyta content z context po `w.id`
 *   4. Per `componentSymbol` wybiera React renderer (np. `w_menu` -> `<WMenu>`)
 *   5. Renderer dostaje content jako prop i renderuje HTML/CSS
 *
 * **Fallback messages** (visible w container body):
 *   - `meta.missing_class` lub `meta.missing_method` -> "Brak metody
 *     `getApiContent` w klasie X" + link do dokumentacji
 *   - Brak React renderera dla `componentSymbol` -> "React-CMS nie ma jeszcze
 *     komponenta dla X" + link do docs
 *   - `meta.invalid_response` -> "getApiContent zwróciło broken response"
 *
 * **Dev banner:** AlignmentChecker subscribuje do `KNOWN_WINDOW_SYMBOLS`
 * (z `components/cms/registry/variants.tsx`) + skanuje `unpublishedWindows`.
 * Gdy okno ma `componentSymbol` którego nie ma w registry -> issue
 * `react_renderer_missing` z hintem jak dodać.
 */
export function WindowRenderer({ window: w, area }: WindowRendererProps) {
  const { windowContents, adminPanelUrl, activeTemplate } = useAdmin();
  const promise = windowContents.get(w.id);

  /* React's `use()` hook — suspend'uje render dopóki promise się nie resolved'a.
     Wrapping `<Suspense>` w `WindowAreaView` pokazuje `<WindowSkeleton />`.
     Gdy brak promise w mapie (okno nie zostało pre-fetched) -> renderujemy
     bezpośrednio fallback message, NIE wywołujemy use() (otherwise infinite suspend). */
  const contentResponse = promise ? use(promise) : undefined;

  return (
    <div
      className={styles.root}
      data-window-symbol={w.componentSymbol}
      data-area-symbol={area}
    >
      <RenderContent
        window={w}
        area={area}
        contentResponse={contentResponse}
        adminPanelUrl={adminPanelUrl}
        activeTemplate={activeTemplate}
      />
    </div>
  );
}

export type WindowRendererProps = {
  window: UnpublishedWindow;
  /**
   * Symbol obszaru w którym renderowane jest okno (np. `heroSmallTop`, `main`).
   * Używany przez `WINDOW_AREA_VARIANTS` do dispatch'u variant rendera per
   * (componentSymbol, area). Bez tej informacji wszystkie instancje okna
   * renderują się tym samym default'owym komponentem.
   */
  area: AreaSymbol;
};

/* ─── Content render + fallback handlers ─────────────────────────────── */
/* Dispatch przez `resolveWindowVariant` z components/cms/registry/variants.tsx
   (single source of truth — dodawanie typu/variantu: zob. dokumentacja header
   pliku variants.tsx). */

function RenderContent({
  window: w,
  area,
  contentResponse,
  adminPanelUrl,
  activeTemplate,
}: {
  window: UnpublishedWindow;
  area: AreaSymbol;
  activeTemplate: string | null;
  /** undefined = brak promise'a w map'ie (race condition lub okno nie pre-fetched),
      null = promise resolved do null (fetch failed). W obu kazach pokazujemy
      fallback "Brak danych z backend'u". */
  contentResponse: WindowContentResponse<unknown> | undefined | null;
  adminPanelUrl: string;
}) {
  /* Case 1: fetch fail całkowicie (network / 500 / klucz API) — nic w contextach. */
  if (!contentResponse) {
    return (
      <FallbackMessage
        title="Brak danych z backend'u"
        message={`Nie udało się pobrać content'u dla okna "${w.name}" (component: ${w.componentSymbol}).`}
        hint="Sprawdź czy backend Imperial odpowiada (/IRESTAPI/{version}/IWINDOWS_MANAGER_CONTROLLER/windowContent/{id}) oraz X-API-Key jest poprawne w .env.local."
        docsAnchor="rest-api"
        adminPanelUrl={adminPanelUrl}
      />
    );
  }

  /* Case 2: backend nie ma klasy controlera. */
  if (contentResponse.meta.missing_class) {
    return (
      <FallbackMessage
        title="Brak kontrolera komponentu"
        message={
          contentResponse.meta.message ??
          `Klasa ${contentResponse.meta.class_name} nie istnieje w /component/${w.componentSymbol}/.`
        }
        hint={`Dodaj plik component/${w.componentSymbol}/${w.componentSymbol}.php z klasą ${contentResponse.meta.class_name} extends ICOMPONENT_CONTROLLER implements ICOMPONENT.`}
        docsAnchor="lyt-areas"
        adminPanelUrl={adminPanelUrl}
      />
    );
  }

  /* Case 3: backend ma controller, ale brak metody getApiContent. */
  if (contentResponse.meta.missing_method) {
    return (
      <FallbackMessage
        title="Brak metody `getApiContent` w komponencie backend"
        message={
          contentResponse.meta.message ??
          `Klasa ${contentResponse.meta.class_name} nie ma metody getApiContent(). Visitor nie może pobrać content'u dla okna "${w.name}".`
        }
        hint={
          `Dodaj public function getApiContent(array $api, array $params) ` +
          `w component/${w.componentSymbol}/${w.componentSymbol}.php. ` +
          `Wzorzec: w_menu.php (zwraca tree menu jako JSON przez IAPI::response).`
        }
        docsAnchor="rest-api"
        adminPanelUrl={adminPanelUrl}
      />
    );
  }

  /* Case 4: response invalid (getApiContent zwróciło coś co nie parsuje
     jako JSON). */
  if (contentResponse.meta.invalid_response) {
    return (
      <FallbackMessage
        title="Niepoprawna odpowiedź z backend"
        message={
          contentResponse.meta.message ??
          `${contentResponse.meta.class_name}::getApiContent() nie zwróciło valid JSON.`
        }
        hint={
          contentResponse.meta.raw_preview
            ? `Pierwsze 200 znaków odpowiedzi: ${contentResponse.meta.raw_preview}`
            : "Sprawdź czy getApiContent używa IAPI::response(true, null, $data, $params, $api['log_id']) zamiast echo HTML."
        }
        docsAnchor="rest-api"
        adminPanelUrl={adminPanelUrl}
      />
    );
  }

  /* Case 4b: pojedyncze okno rzuciło wyjątek w batch path `IAPI_pageBootstrap`.
     Backend łapie `\Throwable` per-okno (żeby jedno zepsute okno nie wywaliło
     całego bootstrap'a) i zwraca `response_window_data: null` + `meta.fetch_error`
     BEZ flag missing_class/missing_method/invalid_response. Bez tego case'a null
     leciał do Case 6 happy-path i renderery dereferencjujące `content` (np.
     WUserAccount na `content.error`) crashowały z TypeError. Typowe przyczyny:
     cross-domain (300003) / cross-language (300004) guard w `IAPI_windowContent`. */
  if (contentResponse.meta.fetch_error) {
    return (
      <FallbackMessage
        title="Błąd pobierania content'u okna"
        message={
          `Backend nie zdołał zbudować content'u dla okna "${w.name}" ` +
          `(component: ${w.componentSymbol})` +
          (contentResponse.meta.code ? ` - kod ${contentResponse.meta.code}` : "") +
          `: ${contentResponse.meta.fetch_error}`
        }
        hint={
          contentResponse.meta.code === 300003 || contentResponse.meta.code === 300004
            ? "Okno jest przypisane do innej domeny / wersji językowej niż renderowana strona. Sprawdź `imp_cms_window.id_domain` / `id_domain_language` dla tego okna - po zapisie układu okno trafiło do strefy renderowanej w innym kontekście językowym."
            : "Sprawdź log backend'u (data/logs) - `getApiContent()` tego komponentu rzucił wyjątek."
        }
        docsAnchor="rest-api"
        adminPanelUrl={adminPanelUrl}
      />
    );
  }

  /* Case 5: backend OK. Resolve rendera przez `resolveWindowVariant`
     (single source of truth — `components/cms/registry/variants.tsx`):
       1) areaOverrides[componentSymbol]?.[area] -> variant per (component, area)
       2) defaults[componentSymbol] -> default renderer
       3) null -> fallback message "Brak renderera Imperial CMS" */
  const Renderer = resolveWindowVariant(w.componentSymbol, area, activeTemplate);
  if (!Renderer) {
    return (
      <FallbackMessage
        title="Brak renderera Imperial CMS"
        message={`Okno typu "${w.componentSymbol}" ma działającą metodę backend (${contentResponse.meta.class_name}) ale Imperial CMS nie ma jeszcze komponentu do wyświetlenia.`}
        hint={
          `Dodaj komponent W${snakeToPascal(w.componentSymbol)} w ` +
          `components/windows/W${snakeToPascal(w.componentSymbol)}/W${snakeToPascal(w.componentSymbol)}.tsx ` +
          `+ zarejestruj w \`windowVariants.defaults\` w components/cms/registry/variants.tsx. ` +
          `Wzorzec: components/windows/WMenu/.`
        }
        docsAnchor="rest-api"
        adminPanelUrl={adminPanelUrl}
      />
    );
  }

  /* Case 6 (happy path): render real content. Unwrapping `response_window_data` —
     to czysty payload z `getApiContent()`, bez metadata wrapper'a (window_id,
     component_symbol, title są dostępne w `data` ale nie potrzebne renderer'owi
     komponentu — on dostaje TYLKO content per-typ). Przekazujemy też title +
     background + area + ContainerOverride żeby variant mógł sam wrappować.

     `ContainerOverride` z DB (`container_symbol`) - resolveContainer mapuje
     symbol z `imp_cms_container.symbol` na React Container component (np.
     'grey_title' -> WDefault, 'empty' -> WEmpty). Null gdy backend nie ma
     `container_symbol` lub symbol nieznany - dispatch lambda używa swojego
     hardcoded fallback'u. */
  const ContainerOverride = resolveContainer(w.containerSymbol);
  return (
    <Renderer
      content={contentResponse.data.response_window_data}
      title={w.name}
      background={w.background ?? null}
      area={area}
      ContainerOverride={ContainerOverride}
    />
  );
}

/* ─── Fallback message component ─────────────────────────────────────── */

function FallbackMessage({
  title,
  message,
  hint,
  docsAnchor,
  adminPanelUrl,
}: {
  title: string;
  message: string;
  hint: string;
  docsAnchor: string;
  adminPanelUrl: string;
}) {
  const t = getMessages(useLocale());
  const docsUrl = `${adminPanelUrl}?admin_page=system_administration&sys=documentation&doc=react_cms#${docsAnchor}`;
  return (
    <div className={styles.fallback} role="status">
      <div className={styles.fallbackTitle}>{title}</div>
      <p className={styles.fallbackMessage}>{message}</p>
      <p className={styles.fallbackHint}>{hint}</p>
      <a
        href={docsUrl}
        target="_blank"
        rel="noopener noreferrer"
        className={styles.fallbackDocsLink}
      >
        {t.common.readDocs}
      </a>
    </div>
  );
}

/* ─── Helpers ────────────────────────────────────────────────────────── */

/** `w_menu_level_one` -> `MenuLevelOne` — pomocnik dla auto-generated hint'ów. */
function snakeToPascal(snake: string): string {
  return snake
    .replace(/^w_/, "")
    .split("_")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join("");
}
